import { createReducer } from '@common/redux-utils';
import {
  ClearUserStartAction,
  ClearUserSuccessAction,
  CreateUserFailureAction,
  CreateUserStartAction,
  CreateUserSuccessAction,
  GetUserFailureAction,
  GetUserStartAction,
  GetUserSuccessAction,
  GetRolesSuccessAction,
  GetRolesFailureAction,
  UserActionType,
  UpdateUserFailureAction,
  UpdateUserStartAction,
  UpdateUserSuccessAction,
  ToggleRowOptionAction
} from './user.actions';
import { ProfileViewModel } from '@models/Auth';
import { OptionsViewModel } from '@models/common.models';

// Defining the state of the page.
// Should be one per page.
// All properties should be readonly.
export interface UserState {
  isSaving: boolean;
  message: string;
  user: ProfileViewModel | null;
  allRoleOptions: Array<OptionsViewModel>;
  selectedRoleOptions?: Array<OptionsViewModel>;
}

// This is the initial state of the page
const initialState: UserState = {
  isSaving: false,
  message: 'Initialized',
  user: {
    id: 0,
    email: '',
    password: undefined,
    firstName: '',
    lastName: '',
    phone: undefined,
    address: undefined,
    roles: [],
    roleOptions: []
  },
  allRoleOptions: [],
  selectedRoleOptions: []
};

/********** Clear **********/
function handleClearUserStart(state: UserState, action: ClearUserStartAction): UserState {
  return state;
}

function handleClearUserSuccess(state: UserState, action: ClearUserSuccessAction): UserState {
  return {
    ...state,
    user: null,
    selectedRoleOptions: undefined
  };
}

/********** Create **********/
function handleCreateUserStart(state: UserState, action: CreateUserStartAction): UserState {
  return {
    ...state,
    isSaving: true
  };
}

function handleCreateUserSuccess(state: UserState, action: CreateUserSuccessAction): UserState {
  return {
    ...state,
    user: {
      ...state.user,
      id: action.id,
      roleOptions: state.selectedRoleOptions
    } as ProfileViewModel,
    isSaving: false,
    message: 'SUCCESS - user profile created'
  };
}

function handleCreateUserFailure(state: UserState, action: CreateUserFailureAction): UserState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    isSaving: false,
    message: 'ERROR - create user profile failed - ' + action.errorMessage
  };
}

/********** Get **********/
function handleGetUserStart(state: UserState, action: GetUserStartAction): UserState {
  return state;
}

function handleGetUserSuccess(state: UserState, action: GetUserSuccessAction): UserState {
  return {
    ...state,
    user: action.user,
    selectedRoleOptions: action.user.roleOptions
  };
}

function handleGetUserFailure(state: UserState, action: GetUserFailureAction): UserState {
  return {
    ...state,
    isSaving: false,
    message: 'ERROR - get user profile failed - ' + action.errorMessage
  };
}

function handleGetRolesSuccess(state: UserState, action: GetRolesSuccessAction): UserState {
  return {
    ...state,
    allRoleOptions: action.roleOptions
  };
}

function handleGetRolesFailure(state: UserState, action: GetRolesFailureAction): UserState {
  return {
    ...state,
    message: 'ERROR - get roles options faile - ' + action.errorMessage
  };
}

/********** Update **********/
function handleUpdateUserStart(state: UserState, action: UpdateUserStartAction): UserState {
  return {
    ...state,
    isSaving: true
  };
}

function handleUpdateUserSuccess(state: UserState, action: UpdateUserSuccessAction): UserState {
  return {
    ...state,
    isSaving: false,
    message: 'SUCCESS - user profile saved'
  };
}

function handleUpdateUserFailure(state: UserState, action: UpdateUserFailureAction): UserState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    isSaving: false,
    message: 'ERROR - save user profile failed - ' + action.errorMessage
  };
}

function handleToggleRowOption(state: UserState, action: ToggleRowOptionAction): UserState {
  if (state.user && state.user.roleOptions) {
    const index = state.user.roleOptions.map(x => x.id).indexOf(action.option.id);
    let newOptions = state.user.roleOptions.slice(); // new options is a copy of the old opts that are already selected
    if (index !== -1) {
      // the item is already selected, remove it from the array
      newOptions = state.user.roleOptions.slice().filter(x => x.id !== action.option.id);
    } else {
      // the item is not in the array, so add it to a copy
      newOptions = state.user.roleOptions.slice().concat([action.option]);
    }

    /* this updates the selectedRoleOptions and the user 
    (when we are editing a user updating the user updates the form)*/
    return {
      ...state,
      selectedRoleOptions: newOptions,
      user: {
        ...state.user,
        roleOptions: newOptions
      } as ProfileViewModel
    };
  }
  return {
    ...state
  };
}

// This creates the reducer as redux wants it w/ a helper function
// There should an entry for each action type
export const UserReducer = createReducer(initialState, {
  /********** Clear **********/
  [UserActionType.ClearUserStart]: handleClearUserStart,
  [UserActionType.ClearUserSuccess]: handleClearUserSuccess,

  /********** Create **********/
  [UserActionType.CreateUserStart]: handleCreateUserStart,
  [UserActionType.CreateUserSuccess]: handleCreateUserSuccess,
  [UserActionType.CreateUserFailure]: handleCreateUserFailure,

  /********** Get **********/
  [UserActionType.GetUserStart]: handleGetUserStart,
  [UserActionType.GetUserSuccess]: handleGetUserSuccess,
  [UserActionType.GetUserFailure]: handleGetUserFailure,
  [UserActionType.GetRolesSuccess]: handleGetRolesSuccess,
  [UserActionType.GetRolesFailure]: handleGetRolesFailure,

  /********** Update **********/
  [UserActionType.UpdateUserStart]: handleUpdateUserStart,
  [UserActionType.UpdateUserSuccess]: handleUpdateUserSuccess,
  [UserActionType.UpdateUserFailure]: handleUpdateUserFailure,
  [UserActionType.ToggleRowOption]: handleToggleRowOption
});



// WEBPACK FOOTER //
// ./src/user/user.reducer.ts